/**
 * LLMModelSelector Component
 *
 * Thin glue layer: maps LLM model data into PickerOption<LLMModelMeta> and
 * delegates collapse/expand/search/sort shell to <ProviderPicker>.
 *
 * Domain-specific concerns kept here:
 *   - Cheapest auto-selection on first load
 *   - Ollama download modal
 *   - Hover RPG preview portal (HoverPreviewCard)
 *   - "Free Only" filter chip + LLM RPG SortBar (passed as extraFilters)
 *   - Avatar URL resolution (buildApiUrl + getAvatarForModel)
 *   - groupBy provider prefix
 *   - renderCard → existing <ModelCard> (preserves avatar + hover RPG preview)
 *   - ModelStatsPanel in the collapsed-state footer
 */

import { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { Cpu, Sparkles } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useOllamaModels } from '../../hooks/useOllamaModels';
import { ModelDownloadModal } from '../ollama/ModelDownloadModal';
import type { LLMModelOption, LLMProvider } from '../../types';
import { PROVIDER_CONFIG } from './llm/providerConfig';
import { getModelDisplayName, getAvatarForModel } from './llm/modelAvatars';
import { isServerlessMode, isLocalMode, isElectronMode } from '../../utils/apiMode';
import { buildApiUrl } from '../../services/urlResolver';
import { ModelCard, HoverPreviewCard, ModelStatsPanel, SortBar } from './llm/ModelComponents';
import type { SortState } from './llm/ModelComponents';
import { getModelAttributes, getModelTotalPrice } from '@/constants/llm/modelAttributes';
import { DEFAULT_VALUES } from '@/constants';
import { ProviderPicker } from '../common/ProviderPicker';
import type { PickerOption } from '../common/ProviderPicker';

// =============================================================================
// TYPES
// =============================================================================

interface LLMModelMeta {
  provider: string;
  /** The backend row, which carries the model's price. */
  model: LLMModelOption;
}

interface LLMModelSelectorProps {
  models: LLMModelOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  showFreeOnly?: boolean;
  /** Hide the "Free Only" filter button (e.g., in serverless mode where free models are not available) */
  hideFreeFilter?: boolean;
  /** LLM provider - when 'ollama', enables model download functionality */
  provider?: LLMProvider;
  /**
   * Fallback auto-selection when no recommended model is available: pick the
   * cheapest model when nothing is selected yet. Defaults to true. The
   * recommended model (see recommendedValue) always takes precedence over this.
   * Set to false to disable the cheapest fallback entirely.
   */
  autoSelectCheapest?: boolean;
  /**
   * Model id to flag as "recommended" and auto-select when nothing is selected.
   * Gets a star badge in the grid and a "(Recommended)" tag in the collapsed
   * row. When omitted, the provider's recommended default is used
   * (DEFAULT_VALUES.llm_model_openrouter for OpenRouter — single source of
   * truth from the backend DEFAULT_MODEL_PER_PROVIDER). Pass an explicit value
   * to override (e.g. a use-case-specific model like the YouTube SEO model).
   */
  recommendedValue?: string;
}

// =============================================================================
// HELPERS
// =============================================================================

const AVATAR_API_ENDPOINT = '/api/v1/llm/avatars';

function getAvatarUrl(modelId: string): string {
  const isLocal = isLocalMode() || isElectronMode();
  const avatarFile = getAvatarForModel(modelId, !isLocal);
  return buildApiUrl(`${AVATAR_API_ENDPOINT}/${avatarFile}`);
}

/**
 * Compute the group key for a model id.
 * - OpenRouter ids are namespaced with "/" (e.g. "meta-llama/llama-3.3-70b") → group by vendor prefix.
 * - Ollama ids use "family:size" (e.g. "gemma2:9b", "gemma2:2b") → group by family so size variants
 *   collapse under one header instead of each landing on its own singleton group/line.
 * - Bare slugs (e.g. RunPod "qwen3-32b-awq") → their own group.
 */
function getModelGroupKey(value: string): string {
  if (value.includes('/')) return value.split('/')[0];
  if (value.includes(':')) return value.split(':')[0];
  return value;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const LLMModelSelector = memo(function LLMModelSelector({
  models,
  value,
  onChange,
  disabled = false,
  label = 'AI Model',
  showFreeOnly = false,
  hideFreeFilter = false,
  provider,
  autoSelectCheapest = true,
  recommendedValue,
}: LLMModelSelectorProps) {
  const { t } = useTranslation();
  const isWeb = isServerlessMode();
  const [filterFree, setFilterFree] = useState(showFreeOnly);
  const [sortBy, setSortBy] = useState<SortState>(null);

  // Ollama model management
  const isOllama = provider === 'ollama';
  const {
    isModelInstalled,
    getModelInfo,
    downloadModel,
    downloadProgress,
    clearProgress,
    ollamaAvailable,
  } = useOllamaModels();
  const [selectedModelForDownload, setSelectedModelForDownload] = useState<string | null>(null);

  // Hover preview state
  const [hoverPreview, setHoverPreview] = useState<{
    modelId: string;
    name: string;
    position: { x: number; y: number };
  } | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleCardHover = useCallback((modelId: string, name: string, rect: DOMRect) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverPreview({
        modelId,
        name,
        position: { x: rect.left + rect.width / 2, y: rect.top },
      });
    }, 300);
  }, []);

  const handleCardLeave = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoverPreview(null);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  // Effective recommended model: an explicit recommendedValue wins; otherwise
  // fall back to the provider's recommended default. Single source of truth:
  // backend DEFAULT_MODEL_PER_PROVIDER -> generated DEFAULT_VALUES. Only
  // OpenRouter has a recommended default here; ollama/runpod keep the cheapest
  // fallback (their recommended ids aren't in this generic list).
  const isOpenRouter = !provider || provider === 'openrouter';
  const effectiveRecommended =
    recommendedValue ?? (isOpenRouter ? DEFAULT_VALUES.llm_model_openrouter : undefined);

  // Auto-select a model when nothing is selected yet:
  //   1. Prefer the recommended model — gives every tab the SAME default
  //      (consistency was the whole point of consolidating this selector).
  //   2. Fall back to the cheapest model (opt-out via autoSelectCheapest=false).
  // A web-locked (":free" in serverless) recommended model is skipped so the
  // user never lands on an unusable model.
  useEffect(() => {
    if (value || !Array.isArray(models) || models.length === 0) return;

    // 1. Recommended default (takes precedence over cheapest)
    if (effectiveRecommended) {
      const rec = models.find((m) => m.value === effectiveRecommended);
      const recWebLocked = isWeb && effectiveRecommended.includes(':free');
      if (rec && rec.disabled !== true && !recWebLocked) {
        onChange(effectiveRecommended);
        return;
      }
    }

    // 2. Cheapest fallback
    if (!autoSelectCheapest) return;
    let cheapest: { value: string; cost: number } | null = null;
    for (const model of models) {
      if (isWeb && model.value.includes(':free')) continue;
      const cost = getModelTotalPrice(model);
      if (cost === null) continue;
      // On the billed website, skip $0-priced models: these are free/stealth
      // models (e.g. "openrouter/owl-alpha") whose id lacks the ":free" suffix,
      // so the filter above misses them and they'd win "cheapest" with cost 0.
      // Desktop keeps them (free models are usable locally).
      if (isWeb && cost <= 0) continue;
      if (!cheapest || cost < cheapest.cost) {
        cheapest = { value: model.value, cost };
      }
    }
    if (cheapest) onChange(cheapest.value);
  }, [value, models, onChange, isWeb, autoSelectCheapest, effectiveRecommended]);

  // Apply free-filter + LLM RPG sort to produce the model list ProviderPicker sees
  const filteredModels = useMemo(() => {
    if (!Array.isArray(models)) return [];
    let filtered = models;
    if (filterFree) filtered = filtered.filter((m) => m.value.includes(':free'));
    if (sortBy) {
      const dir = sortBy.direction === 'asc' ? 1 : -1;
      return [...filtered].sort((a, b) => {
        if (a.value === value) return -1;
        if (b.value === value) return 1;
        if (sortBy.attribute === 'name') return dir * a.label.localeCompare(b.label);
        if (sortBy.attribute === 'provider') {
          const provA = getModelGroupKey(a.value);
          const provB = getModelGroupKey(b.value);
          const cmp = provA.localeCompare(provB);
          return cmp !== 0 ? dir * cmp : a.label.localeCompare(b.label);
        }
        if (sortBy.attribute === 'cheapest') {
          const costA = getModelTotalPrice(a);
          const costB = getModelTotalPrice(b);
          if (costA === null && costB === null) return a.label.localeCompare(b.label);
          if (costA === null) return 1;
          if (costB === null) return -1;
          return dir * (costA - costB);
        }
        // No benchmark row, or an unknown ATK/DEF price: sorted last.
        const attrsA = getModelAttributes(a);
        const attrsB = getModelAttributes(b);
        const scoreA = attrsA ? attrsA[sortBy.attribute] : null;
        const scoreB = attrsB ? attrsB[sortBy.attribute] : null;
        if (scoreA === null && scoreB === null) return a.label.localeCompare(b.label);
        if (scoreA === null) return 1;
        if (scoreB === null) return -1;
        return dir * (scoreA - scoreB);
      });
    }
    // Default: selected first, then free/paid order by mode, then alphabetical
    return [...filtered].sort((a, b) => {
      if (a.value === value) return -1;
      if (b.value === value) return 1;
      const aFree = a.value.includes(':free');
      const bFree = b.value.includes(':free');
      if (isWeb) {
        if (aFree && !bFree) return 1;
        if (!aFree && bFree) return -1;
      } else {
        if (aFree && !bFree) return -1;
        if (!aFree && bFree) return 1;
      }
      return a.label.localeCompare(b.label);
    });
  }, [models, filterFree, sortBy, value, isWeb]);

  // Map to PickerOption — ProviderPicker uses these for search + groupBy only;
  // the actual card rendering is overridden via renderCard below.
  const pickerOptions = useMemo<PickerOption<LLMModelMeta>[]>(() => {
    return filteredModels.map((m) => ({
      value: m.value,
      label: m.label || getModelDisplayName(m.value),
      iconUrl: getAvatarUrl(m.value),
      disabled: m.disabled === true,
      recommended: effectiveRecommended !== undefined && m.value === effectiveRecommended,
      meta: { provider: getModelGroupKey(m.value), model: m },
    }));
  }, [filteredModels, effectiveRecommended]);

  // groupBy: by LLM provider prefix (matches existing behaviour)
  // When sortBy is active (non-provider), flatten into _all — mirrors original code.
  const groupByFn = useCallback(
    (opt: PickerOption<LLMModelMeta>): string | null => {
      if (sortBy && sortBy.attribute !== 'provider') return null; // _ungrouped → flat list
      return opt.meta?.provider ?? null;
    },
    [sortBy]
  );

  // extraFilters: RPG SortBar + Free Only toggle
  const extraFilters = (
    <>
      <SortBar sortBy={sortBy} onSortChange={setSortBy} />
      {!hideFreeFilter && !isWeb && (
        <button
          type="button"
          onClick={() => setFilterFree(!filterFree)}
          className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 shrink-0 min-h-[44px]
            ${filterFree ? 'bg-green-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
        >
          <Sparkles className="w-3.5 h-3.5" />
          {t('llm.freeOnly', 'Free Only')}
        </button>
      )}
    </>
  );

  // renderCard: use existing ModelCard to preserve avatar + hover RPG preview + MiniStats
  const renderCard = useCallback(
    (opt: PickerOption<LLMModelMeta>, isSelected: boolean, onClick: () => void) => {
      const webLocked = isWeb && opt.value.includes(':free');
      const model = opt.meta?.model;
      if (!model) throw new Error(`LLM picker option ${opt.value} was built without its model row`);
      return (
        <ModelCard
          key={opt.value}
          model={model}
          label={opt.label}
          isSelected={isSelected}
          isFree={opt.value.includes(':free')}
          isWebLocked={webLocked}
          onClick={() => {
            if (webLocked) return;
            handleCardLeave();
            if (isOllama && ollamaAvailable && !isModelInstalled(opt.value)) {
              setSelectedModelForDownload(opt.value);
              return;
            }
            onClick();
          }}
          disabled={disabled || opt.disabled === true}
          onHover={handleCardHover}
          onLeave={handleCardLeave}
          needsDownload={isOllama && ollamaAvailable && !isModelInstalled(opt.value)}
          recommended={opt.recommended === true}
        />
      );
    },
    [isWeb, disabled, isOllama, ollamaAvailable, isModelInstalled, handleCardHover, handleCardLeave]
  );

  // Collapsed preview extras: ModelStatsPanel for the selected model
  const selectedModel = Array.isArray(models) ? models.find((m) => m.value === value) : undefined;
  const hoveredModel =
    hoverPreview && Array.isArray(models)
      ? models.find((m) => m.value === hoverPreview.modelId)
      : undefined;
  const selectedProvider = value ? value.split('/')[0] : '';
  const selectedProviderConfig = PROVIDER_CONFIG[selectedProvider];

  return (
    <div className="space-y-2">
      <label className="form-label flex items-center gap-2">
        <Cpu className="h-4 w-4" />
        {label}
      </label>

      <ProviderPicker<LLMModelMeta>
        options={pickerOptions}
        value={value || null}
        onChange={onChange}
        disabled={disabled}
        sortKeys={[]}
        searchable={true}
        extraFilters={extraFilters}
        renderCard={renderCard}
        groupBy={groupByFn}
        cardMinWidth={140}
        emptyState={
          <p className="text-center text-secondary-500 py-8">
            {t('llm.noModels', 'No models found')}
          </p>
        }
      />

      {/* Collapsed stats panel: shown below the ProviderPicker collapsed row */}
      {selectedModel && (
        <div
          className={`border rounded-xl border-white/10 ${selectedProviderConfig ? selectedProviderConfig.bgColor : 'bg-black/30'}`}
        >
          <div className="px-3 pb-3 pt-1">
            <ModelStatsPanel model={selectedModel} />
          </div>
        </div>
      )}

      {/* Hover Preview Portal */}
      {hoverPreview && hoveredModel && (
        <HoverPreviewCard
          model={hoveredModel}
          modelName={hoverPreview.name}
          position={hoverPreview.position}
        />
      )}

      {/* Ollama Download Modal */}
      {isOllama && selectedModelForDownload && (
        <ModelDownloadModal
          model={
            getModelInfo(selectedModelForDownload) || {
              id: selectedModelForDownload,
              installed: false,
              size_gb: 0,
              vram_gb: 0,
              description: 'Unknown model',
            }
          }
          progress={downloadProgress[selectedModelForDownload] || null}
          isOpen={true}
          onConfirm={async () => {
            const success = await downloadModel(selectedModelForDownload);
            if (success) {
              onChange(selectedModelForDownload);
              setTimeout(() => {
                clearProgress(selectedModelForDownload);
                setSelectedModelForDownload(null);
              }, 1500);
            }
          }}
          onCancel={() => {
            clearProgress(selectedModelForDownload);
            setSelectedModelForDownload(null);
          }}
        />
      )}
    </div>
  );
});

export default LLMModelSelector;
